import React, { Component } from 'react';
import { Player, ControlBar } from 'video-react';
import 'video-react/dist/video-react.css'; // import css
import PropTypes from 'prop-types';
import Switch from 'react-switch';
import _ from 'lodash';
import cn from 'classnames';
import PlayArrow from '@material-ui/icons/PlayArrow';
import Pause from '@material-ui/icons/Pause';

import { getPrettyTimeDisplayFromNumber } from '../../helpers/formatting';
import { getEmotionColor, getEmotionalBreakdownFromMessages, getParticipantBreakdown } from '../../helpers/emotion_helpers';

import './CallInsights.scss';

class CallInsights extends Component {
  static propTypes = {
    call: PropTypes.object,
    setHighlightedEmotion: PropTypes.func.isRequired,
    setHighlightedVariable: PropTypes.func.isRequired,
    setHighlightedParticipant: PropTypes.func.isRequired,
    highlightedEmotion: PropTypes.string,
    highlightedVariable: PropTypes.string,
    highlightedParticipant: PropTypes.object,
    audioTime: PropTypes.number.isRequired,
    audioPaused: PropTypes.bool.isRequired,
    startTime: PropTypes.func.isRequired,
    stopTime: PropTypes.func.isRequired,
    setVideoRef: PropTypes.func.isRequired,
    video: PropTypes.object,
    canLoadAudioAndVideo: PropTypes.bool.isRequired,
  };

  state = {
    showLive: true,
  };

  clickParticipant = (participant) => {
    const { highlightedParticipant } = this.props;
    this.props.setHighlightedParticipant(highlightedParticipant?.id === participant?.id ? null : participant);
  };
  clickEmotion = (emotion) => {
    const { highlightedEmotion } = this.props;
    this.props.setHighlightedEmotion(highlightedEmotion === emotion ? null : emotion);
  };
  clickVariable = (newVariable) => {
    const { highlightedVariable } = this.props;
    this.props.setHighlightedVariable(highlightedVariable === newVariable ? null : newVariable);
  };

  messageMeetsFilters = (msg) => {
    const meetsTimeFilter = !this.state.showLive || !this.props.audioTime || this.props.audioTime > msg.start_time;
    const meetsEmotionFilter = !this.props.highlightedEmotion || this.props.highlightedEmotion === msg.emotion;
    const meetsParticipantFilter = !this.props.highlightedParticipant || this.props.highlightedParticipant?.id === msg.speaker?.id;
    return meetsTimeFilter && meetsEmotionFilter && meetsParticipantFilter;
  };

  getStats = () => {
    const { call } = this.props;
    const { messages, summary } = call;
    // TODO, optimize this function
    const allStats = [
      {
        num: _.filter(messages, (i) => i.is_rep_question).length,
        visibleNum: _.filter(messages, (i) => this.messageMeetsFilters(i) && i.is_rep_question).length,
        delta: _.get(summary, '0.rep_questions_delta') === -1 ? 0 : _.get(summary, '0.rep_questions_delta') || 0, // UNNECESSARY -1
        label: 'Rep Questions',
        variable: 'is_rep_question',
        showPrev: false,
        showNext: true,
      },
      {
        num: _.filter(messages, (i) => i.is_customer_question).length,
        visibleNum: _.filter(messages, (i) => this.messageMeetsFilters(i) && i.is_customer_question).length,
        delta: _.get(summary, 'customer_questions_delta') === -1 ? 0 : _.get(summary, 'customer_questions_delta') || 0, // UNNECESSARY -1
        label: 'Customer Questions',
        variable: 'is_customer_question',
        showPrev: false,
        showNext: true,
      },
      {
        num: _.filter(messages, (i) => i.is_rep_pitch).length,
        visibleNum: _.filter(messages, (i) => this.messageMeetsFilters(i) && i.is_rep_pitch).length,
        delta: _.get(summary, 'pitches_delta') === -1 ? 0 : _.get(summary, 'pitches_delta') || 0, // UNNECESSARY -1
        label: 'Pitch Attempts',
        variable: 'is_rep_pitch',
        showPrev: false,
        showNext: true,
      },
      {
        num: _.filter(messages, (i) => i.is_unsure).length,
        visibleNum: _.filter(messages, (i) => this.messageMeetsFilters(i) && i.is_unsure).length,
        delta: _.get(summary, 'unsure_delta') === -1 ? 0 : _.get(summary, 'unsure_delta') || 0, // UNNECESSARY -1
        label: 'Coaching Opportunities',
        variable: 'is_unsure',
        showPrev: true,
        showNext: false,
      },
      {
        num: _.filter(messages, (i) => i.is_customer_issue).length,
        visibleNum: _.filter(messages, (i) => this.messageMeetsFilters(i) && i.is_customer_issue).length,
        delta: _.get(summary, 'issues_delta') === -1 ? 0 : _.get(summary, 'issues_delta') || 0, // UNNECESSARY -1
        label: 'Customer Issues',
        variable: 'is_customer_issue',
        showPrev: true,
        showNext: false,
      },
    ];
    const filteredStats = _.filter(allStats, (stat) => stat.num);
    return _.orderBy(filteredStats, 'visibleNum', 'desc');
  };

  render() {
    const { call, audioTime, audioPaused, highlightedEmotion, highlightedParticipant, highlightedVariable } = this.props;
    const { showLive } = this.state;
    const participantBreakdown = getParticipantBreakdown(call, {
      participant: highlightedParticipant,
      emotion: highlightedEmotion,
      variable: highlightedVariable,
      timeWindowEnd: showLive ? audioTime : null,
    });
    const emotionalBreakdown = getEmotionalBreakdownFromMessages(call, {
      participant: highlightedParticipant,
      emotion: highlightedEmotion,
      variable: highlightedVariable,
      timeWindowEnd: showLive ? audioTime : null,
    });
    const stats = this.getStats();
    const video_s3_url = call?.recording?.video_s3_url;

    return (
      <div className={cn('call-insights-outer-container el inner-panel', { 'has-video': video_s3_url })}>
        {video_s3_url && (
          <div className='primary-card card video-card'>
            {this.props.canLoadAudioAndVideo && (
              <Player onReady={() => console.log('ready')} ref={this.props.setVideoRef} muted>
                <ControlBar disableCompletely />
                <source src={video_s3_url} />
              </Player>
            )}
            <div
              onClick={audioPaused ? this.props.startTime : this.props.stopTime}
              className={cn('cover', { start: !audioTime, paused: audioPaused })}
            >
              {audioPaused ? <PlayArrow /> : <Pause />}
            </div>
          </div>
        )}
        <div className='secondary-cards'>
          <div className='card breakdown-card emotions-card'>
            <div className='header'>
              <div className='label'>
                {highlightedParticipant
                  ? `${highlightedParticipant?.name}${
                      highlightedParticipant?.name?.toLowerCase().charAt(highlightedParticipant.length - 1) === 's' ? "'" : "'s"
                    }`
                  : "Participants'"}{' '}
                Emotions
              </div>
              <div className='controls'>
                <Switch
                  onChange={() => this.setState({ showLive: !showLive })}
                  checked={showLive}
                  height={14}
                  width={24}
                  onColor='#333'
                  offColor='#ccc'
                  className='switch'
                  checkedIcon={false}
                  uncheckedIcon={false}
                />
              </div>
            </div>
            <div>
              <div className='labels-container'>
                {_.map(emotionalBreakdown, ({ emotion, percentage, duration }) => {
                  const isActive = highlightedEmotion && highlightedEmotion === emotion;
                  const isInactive = highlightedEmotion && highlightedEmotion !== emotion;
                  return (
                    <div
                      key={emotion}
                      // onMouseEnter={() => this.hoverOn(emotion)}
                      onClick={() => this.clickEmotion(emotion)}
                      className={cn('label', {
                        active: isActive,
                        inactive: isInactive || !percentage,
                      })}
                    >
                      <div className='main'>
                        <div style={{ backgroundColor: getEmotionColor(emotion) }} className='dot' />
                        <div
                          key={emotion}
                          style={{
                            backgroundColor: getEmotionColor(emotion),
                            width: `${percentage < -0.5 ? 0 : percentage}%`,
                          }}
                          className={cn('faded-bar', {
                            active: highlightedEmotion && highlightedEmotion === emotion,
                            inactive: highlightedEmotion && highlightedEmotion !== emotion,
                          })}
                        />
                        <div className='emotion'>
                          {emotion.charAt(0).toUpperCase()}
                          {emotion.slice(1)}
                          {highlightedEmotion === emotion && <div className='cancel'>x</div>}
                        </div>
                      </div>
                      <div className='secondary'>
                        <div className={cn('ratio', { tiny: percentage < 0.5 })}>
                          {!percentage ? '0s' : percentage < 0.5 ? '<1s' : getPrettyTimeDisplayFromNumber(duration)}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
          <div className='card breakdown-card'>
            <div className='header '>
              <div className='label '>STATEMENT TYPES</div>
            </div>
            <div className='labels-container'>
              {_.map(stats, ({ variable, label, delta, num, visibleNum }, idx) => {
                const isActive = highlightedVariable === variable;
                const isInactive = !isActive && highlightedVariable;
                return (
                  <div
                    key={idx}
                    onClick={() => this.clickVariable(variable)}
                    className={cn('label', {
                      active: isActive,
                      inactive: isInactive || !visibleNum,
                    })}
                  >
                    <div className='main'>
                      <div style={{ backgroundColor: isActive ? '#333' : '#ddd' }} className='dot' />
                      <div className='emotion'>
                        <div className='stat-label'>{label}</div>
                      </div>
                    </div>
                    <div className='secondary'>
                      <div className='ratio'>{visibleNum}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
        <div className='tertiary-cards'>
          <div className='card breakdown-card'>
            <div className='header'>
              <div className='label'>{highlightedEmotion ? highlightedEmotion + ' ' : ''}Talk Time</div>
            </div>
            <div>
              <div className='labels-container'>
                {_.map(participantBreakdown, ({ percentage, participant, duration }) => (
                  <div
                    key={participant?.id}
                    onClick={() => this.clickParticipant(participant)}
                    className={cn('label', {
                      active: highlightedParticipant && highlightedParticipant?.id === participant?.id,
                      inactive: highlightedParticipant && highlightedParticipant?.id !== participant?.id,
                    })}
                  >
                    <div className='main'>
                      <div style={{ backgroundColor: '#e5e5e5' }} className='dot' />
                      <div
                        key={participant?.id}
                        style={{
                          backgroundColor: highlightedEmotion ? getEmotionColor(highlightedEmotion) : '#e5e5e5',
                          width: `${percentage < -0.5 ? 0 : percentage}%`,
                        }}
                        className={cn('faded-bar', {
                          active: highlightedParticipant && highlightedParticipant?.id === participant?.id,
                          inactive: highlightedParticipant && highlightedParticipant?.id !== participant?.id,
                        })}
                      />
                      <div className='emotion'>
                        {participant?.name}
                        {highlightedParticipant?.id === participant?.id && <div className='cancel'>x</div>}
                      </div>
                    </div>
                    <div className='secondary'>
                      <div className={cn('ratio', { tiny: duration < 0.5 })}>
                        {!percentage ? '0s' : percentage < 0.5 ? '<1s' : getPrettyTimeDisplayFromNumber(duration)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  getTalkTimeBreakdown = () => {
    const { call } = this.props;
    const { messages } = call;
    const userTime = _.sum(
      _.map(
        _.filter(messages, (c) => c.is_customer),
        'duration'
      )
    );
    const repTime = _.sum(
      _.map(
        _.filter(messages, (c) => !c.is_customer),
        'duration'
      )
    );
    return {
      userTime,
      repTime,
      totalTime: userTime + repTime,
    };
  };
}

export default CallInsights;
